import { Product } from './interfaces';

/**
 * Israeli Consumer Protection Law (amendment 58) agreements.
 *
 * The API marks a flight offer that is NOT covered by the standard Israeli
 * cancellation rules with `is_israel_law_enabled: true`, next to `flightsInfo`
 * and `pax` on the flight view response. The cart carries it in two places:
 *
 *  - product level: `product_data.is_israel_law_enabled = true` wherever the
 *    cart slice writes `flightsInfo` + `pax` into a product's product_data
 *    (flight products, one-way legs, a package's flight_package and the
 *    dynamic_package itself);
 *  - order level: `ShoppingCartV2.userAgreements`, rebuilt from the products on
 *    every cart change — one entry per top-level cart product holding the flag,
 *    removed entirely once no such product is left.
 */
export const ISRAEL_LAW_AGREEMENT_KEY = 'is_israel_law_enabled';

export type AgreementKey = typeof ISRAEL_LAW_AGREEMENT_KEY;

export interface ProductWithAgreement {
  identifier: string;
  agreement_key: AgreementKey;
}

export interface UserAgreements {
  /** Logged-in agent id (`config.agentInfo.agent.id`); '' when no agent is logged in. */
  user_id: string;
  products_with_agreements: ProductWithAgreement[];
}

/**
 * The opt-in site switch (`siteConfig.shareB2C.enable_isreal_law` = 1, number
 * or string). 0 / missing turns every part of the flow off.
 */
export const isIsraelLawFeatureEnabled = (siteConfig?: {
  shareB2C?: { enable_isreal_law?: number | string };
}): boolean => Number(siteConfig?.shareB2C?.enable_isreal_law) === 1;

/** Whether a flight view response (`{ flightsInfo, pax, … }`) carries the flag. */
export const isIsraelLawFlight = (flightResponse?: {
  is_israel_law_enabled?: boolean;
}): boolean => flightResponse?.is_israel_law_enabled === true;

/** Whether a cart product, or any of its sub-products, carries the flag. */
export const hasIsraelLawAgreement = (product?: Product): boolean =>
  !!product &&
  (product.product_data?.is_israel_law_enabled === true ||
    (product.subProducts ?? []).some(hasIsraelLawAgreement));

/**
 * A top-level identifier as a string — dynamic-package flight identifiers can
 * arrive wrapped as `{ t: '<base64>' }`.
 */
const identifierToString = (identifier: unknown): string | undefined => {
  if (typeof identifier === 'string') return identifier || undefined;
  const wrapped = (identifier as { t?: unknown })?.t;
  return typeof wrapped === 'string' && wrapped ? wrapped : undefined;
};

/**
 * Builds the order-level `userAgreements` from the cart products, or
 * `undefined` when no product carries an agreement (the key is then removed).
 * Pure — safe inside reducers. `userId` is the logged-in agent id.
 */
export const buildUserAgreements = (
  products: Product[] | undefined,
  userId = ''
): UserAgreements | undefined => {
  const seen = new Set<string>();
  const products_with_agreements: ProductWithAgreement[] = [];

  (products ?? []).forEach((product) => {
    const identifier = identifierToString(product?.identifier);
    if (!identifier || seen.has(identifier) || !hasIsraelLawAgreement(product)) {
      return;
    }
    seen.add(identifier);
    products_with_agreements.push({
      identifier,
      agreement_key: ISRAEL_LAW_AGREEMENT_KEY,
    });
  });

  return products_with_agreements.length
    ? { user_id: userId, products_with_agreements }
    : undefined;
};

/**
 * `userAgreements.user_id` for the logged-in agent (`agentInfo.agent.id`),
 * '' when no agent is logged in. The single place that decides which field
 * identifies the user.
 */
export const getUserAgreementsUserId = (agentInfo: unknown): string => {
  const id = (agentInfo as { agent?: { id?: unknown } } | null | undefined)
    ?.agent?.id;
  return id === undefined || id === null || id === '' ? '' : String(id);
};

/**
 * Human label for a cart product, used to list the affected products in the
 * save / copy-offer notices: the product name, else the flight route.
 */
export const getAgreementProductLabel = (product?: Product): string => {
  if (!product) return '';
  if (product.name) return product.name;
  const flightsInfo =
    product.product_data?.flightsInfo ?? product.product_data?.flights;
  const from = flightsInfo?.flightFrom?.[0]?.from?.name;
  const legs = flightsInfo?.flightFrom;
  const to = legs?.[legs.length - 1]?.to?.name;
  return from && to ? `${from} → ${to}` : '';
};
